"use client";

import { useEffect, useState } from "react";

// The Workshop's audience is effectively Windows-only (FiveM), so we render
// every keybind label in Windows form (Ctrl/Alt/Shift) on every platform —
// including macOS. Flip to `false` to restore OS-aware labels; the detection
// logic below stays intact so that's a one-line change.
const FORCE_WINDOWS = true;

const MAC_SYMBOL_TO_WINDOWS_TOKEN: Record<string, string> = {
  "⌘": "Ctrl",
  "⌥": "Alt",
  "⇧": "Shift",
};

const WINDOWS_MODIFIER_ORDER = ["Ctrl", "Alt", "Shift"] as const;

function detectMacLike(): boolean | null {
  if (typeof navigator === "undefined") return null;
  const nav = navigator as Navigator & { userAgentData?: { platform?: string } };
  const platform = nav.userAgentData?.platform ?? navigator.platform ?? navigator.userAgent ?? "";
  return /mac|iphone|ipad|ipod/i.test(platform);
}

export function useIsMacLike() {
  const [isMacLike, setIsMacLike] = useState<boolean | null>(detectMacLike);

  useEffect(() => {
    setIsMacLike(detectMacLike());
  }, []);

  // Returning `false` (never `null`) keeps labels in Windows form on the server
  // and the first client render too, so there's no Mac-form flash.
  return FORCE_WINDOWS ? false : isMacLike;
}

export function formatShortcutLabel(shortcut: string, isMacLike: boolean | null) {
  const macLike = FORCE_WINDOWS ? false : isMacLike;
  if (macLike !== false) return shortcut;

  const modifiers = new Set<string>();
  let key = "";

  for (const char of shortcut) {
    const mapped = MAC_SYMBOL_TO_WINDOWS_TOKEN[char];
    if (mapped) {
      modifiers.add(mapped);
      continue;
    }
    if (char.trim().length === 0) continue;
    key += char;
  }

  const orderedModifiers = WINDOWS_MODIFIER_ORDER.filter((token) => modifiers.has(token));
  const normalizedKey = key.trim();
  if (!normalizedKey) return orderedModifiers.join("+");
  return [...orderedModifiers, normalizedKey].join("+");
}

// Matches a shortcut embedded in prose: one or more Mac modifier symbols
// followed by a single key character (e.g. "⌘K", "⇧⌘Z").
const SHORTCUT_IN_TEXT = /[⌘⌥⇧]+[A-Za-z0-9]/g;

/**
 * Rewrites Mac shortcut tokens embedded inside a sentence to their Windows
 * form on non-Mac platforms (e.g. "Open with ⌘K." → "Open with Ctrl+K.").
 * Leaves the text untouched on Mac or before the platform is known.
 */
export function formatShortcutsInText(text: string, isMacLike: boolean | null) {
  const macLike = FORCE_WINDOWS ? false : isMacLike;
  if (macLike !== false) return text;
  return text.replace(SHORTCUT_IN_TEXT, (token) => formatShortcutLabel(token, macLike));
}
